import React, { useCallback, useEffect, useState } from 'react';

import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native';

import { useSelector } from 'react-redux';

import { differenceInSeconds } from 'date-fns';

import { selectLayoutConfig } from 'store/config/selectors';
import { selectCurrentTime } from 'store/timer/selectors';

import useUpdateEffect from 'utils/hooks/useUpdateEffect';

import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles from './styles';

type Props = {
    startTime: Date;
    endTime: Date;
    label: string;
    isLive: boolean;
    buttonMinWidth: number;
    style?: StyleProp<ViewStyle>;
    onPress(): void;
    testID?: string;
};

function LiveProgressButton({
    startTime,
    endTime,
    label,
    isLive,
    buttonMinWidth,
    style,
    onPress,
    testID,
}: Props) {
    const currentTime = useSelector(selectCurrentTime);
    const { primary } = useSelector(selectLayoutConfig);
    const [watchProgressWidth, setWatchProgressWidth] = useState(0);
    const [buttonWidth, setButtonWidth] = useState(buttonMinWidth);

    useEffect(() => {
        if (currentTime) {
            const now = new Date(currentTime * 1000);
            const progress =
                (buttonWidth * differenceInSeconds(now, startTime)) /
                differenceInSeconds(endTime, startTime);

            setWatchProgressWidth(progress <= buttonWidth ? progress : 0);
        }
    }, [startTime, endTime, buttonWidth, currentTime]);

    useUpdateEffect(() => {
        if (!isLive) {
            setWatchProgressWidth(0);
        }
    }, [isLive]);

    const onTouchableOpacityLayout = useCallback(
        (event) => {
            const { width } = event.nativeEvent.layout;

            if (width !== buttonWidth) {
                setButtonWidth(width);
            }
        },
        [buttonWidth],
    );

    return (
        <TouchableOpacity
            style={[
                styles.buttonWatch,
                style,
                { minWidth: buttonMinWidth, backgroundColor: primary },
            ]}
            onPress={onPress}
            onLayout={onTouchableOpacityLayout}
            testID={testID}
            accessibilityRole="button"
        >
            <View
                style={[
                    styles.buttonWatchProgress,
                    { width: watchProgressWidth },
                ]}
            />
            <Text bold category={ETextCategory.body1} color={ETextColor.black}>
                {label}
            </Text>
        </TouchableOpacity>
    );
}

export default LiveProgressButton;
